import { useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { selectUserRoles } from "../../SharedModule/redux/reducers/auth.reducer";
import {
  fetchMetaTimes,
  selectMetaTimesData,
  setMetaTimes,
} from "../redux/reducers/metaTimes.reducer";
import {
  hasTimeAdminAccess,
  hasTimeViewerAccess,
} from "../utils/timesUtils";
import { isDeliveryTimeAdminChecker } from "../../SharedModule/utils/accessUtils";

/**
 * Idempotent hook — loads Times metadata (pay periods, task types, user assignees,
 * non-billable reasons, user list, optional TC filters) exactly once per session.
 *
 * Safe to call from multiple components (TimesPage, useUserProfile, useUsers).
 * Only the first component to mount when timesDataLoaded === false triggers
 * the fetch; subsequent calls are no-ops.
 */
export const useTimesMetadata = () => {
  const dispatch = useDispatch();
  const { rolesLoaded, userRolesList, activeUserId } = useSelector(selectUserRoles);
  const { timesDataLoaded } = useSelector(selectMetaTimesData);

  useEffect(() => {
    if (!rolesLoaded || timesDataLoaded) return;
    const id = activeUserId;
    if (!id) return;

    const isTimeAdmin = hasTimeAdminAccess(userRolesList);
    const isTimeViewer = hasTimeViewerAccess(userRolesList);
    const isDeliveryTimeAdmin = isDeliveryTimeAdminChecker(userRolesList);

    (async () => {
      const metaTimes = await fetchMetaTimes(
        id,
        isTimeAdmin,
        isTimeViewer,
        isDeliveryTimeAdmin
      );
      dispatch(setMetaTimes(metaTimes));
      localStorage.setItem("metaTaskTypes", JSON.stringify(metaTimes.taskTypes));
    })();
  }, [rolesLoaded, timesDataLoaded]); // eslint-disable-line react-hooks/exhaustive-deps
};
